import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import type React from 'react';

import type { TimeDimension } from '~/services/dimensions';
import type { Version } from '~/services/exports';
import type { DimensionType } from '~/types/api-types';
import type { Filter, FilterName } from '~/app/routes/view-page';
import { FilterDialogRaw } from '../filter-dialog';
import FindDimension from '../find-dimension';
import VersionForm from '../version-form';

// Props for the FilterSection component
interface FilterSectionProps {
  filters: Filter[];
  timeDimensionId: number | null;
  dimensionTypes: DimensionType[];
  setAppliedFilters: (filters: Filter[]) => void;
  setTimeDimensionId: (id: number | null) => void;
}

// Stateless FilterSection component
export const FilterSection = ({
  filters,
  timeDimensionId,
  dimensionTypes,
  setAppliedFilters,
  setTimeDimensionId,
}: FilterSectionProps) => {
  function findUnusedFilterName(): FilterName {
    const appliedFilterNames = filters.map((filter) => filter.name);
    return FilterDialogRaw.ALL_AVAILABLE_FILTERS.find(
      (name) => !appliedFilterNames.includes(name) && name !== 'Secret'
    )!;
  }

  function removeFilter(filter: Filter): void {
    const updatedFilters = filters.filter((f) => f.id !== filter.id);
    setAppliedFilters(updatedFilters);
  }

  function findLastFilterName(): FilterName {
    return filters[filters.length - 1].name;
  }

  function createFilter(name: FilterName, value = ''): Filter {
    const id = filters.reduce((max, current) => Math.max(max, current.id), 0) + 1;
    return { name, value, id };
  }

  function addFilter(): void {
    let filterName = findUnusedFilterName();
    if (!filterName) {
      filterName = findLastFilterName();
    }
    const updatedFilters = [...filters, createFilter(filterName)];
    setAppliedFilters(updatedFilters);
  }

  function renderFilter(filter: Filter): React.ReactNode {
    const dimensionType = dimensionTypes.find(
      (x) =>
        (filter.name === 'CRF' && x.name === 'CRFNode') ||
        (filter.name === 'NFR' && x.name === 'NFRcode') ||
        (filter.name === 'Undergrupp' && x.name === 'Subgroup') ||
        filter.name === x.name
    );

    const updateFilters = (name: FilterName, value: string) => {
      const updatedFilters = [...filters];
      const changeFilter = updatedFilters.find((x) => x.id === filter.id)!;
      const changedNameWithValue = changeFilter.name !== name && value.length !== 0;

      changeFilter.name = name;
      changeFilter.value = changedNameWithValue ? '' : value;

      if (updatedFilters.every((x) => x.value)) {
        let filterName = findUnusedFilterName();
        if (!filterName) {
          filterName = findLastFilterName();
        }
        updatedFilters.push(createFilter(filterName));
      }

      setAppliedFilters(updatedFilters);
    };

    if (filter.name === 'Version') {
      const onVersionChange = (s: { version: Version; timeDimension: TimeDimension }) => {
        const version = s.timeDimension.name + s.version.name;
        updateFilters('Version', version);
        setTimeDimensionId(s.timeDimension.id);
      };

      return (
        <Box key={filter.id} sx={{ flexGrow: 1 }}>
          <Grid size="grow">
            <VersionForm onSelect={onVersionChange} />
          </Grid>
        </Box>
      );
    }

    const onSelectChange = (e: SelectChangeEvent<FilterName>) => {
      updateFilters(e.target.value as FilterName, filter.value);
    };

    const onValueChange = (value: string) => {
      updateFilters(filter.name, value);
    };

    return (
      <Box key={filter.id} sx={{ flexGrow: 1 }}>
        <Grid alignItems="flex-end" container spacing={1}>
          <Grid size={{ sm: 12, md: 2 }}>
            <Select
              variant="standard"
              fullWidth={true}
              aria-label="Dimensionstyp"
              value={filter.name}
              onChange={onSelectChange}
            >
              {FilterDialogRaw.ALL_AVAILABLE_FILTERS.filter((x) => x !== 'Version' && x !== 'Secret').map((x) => (
                <MenuItem value={x} key={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <FindDimension
            timeDimensionId={timeDimensionId}
            dimensionTypeId={dimensionType ? dimensionType.id : null}
            onSelect={onValueChange}
          />
          <Grid size={'auto'}>
            <IconButton onClick={() => removeFilter(filter)} size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Grid size="grow" flexDirection={'row'}>
      {filters.map((filter) => renderFilter(filter))}
      <Grid key={'addFilter'} size="grow" alignItems="center">
        <IconButton onClick={addFilter} size="large">
          <AddIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};
